import { usePersistedState } from "@/lib/persistedFilters";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Download, Loader2, Lock, Mail, Plus, Search, Send, Trash2, Upload, XCircle } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MODELS, MODEL_LABEL, fmtPrice, groupByCategory, isPh } from "./priceUtils";
import { CellDialog, ItemDialog } from "./PriceDialogs";
import { ImportDialog, NewBatchDialog, ReviewDialog } from "./BatchDialogs";
import { PriceInsightsDialog } from "./PriceInsightsDialog";

const errMsg = (e, d) => formatApiErrorDetail(e.response?.data?.detail) || d;

function CellView({ c, muted }) {
  if (!c) return null;
  if (c.na) return <span className="text-slate-400">N/A</span>;
  if (isPh(c)) return <span className="font-bold text-red-600" data-testid="price-placeholder">$9,999</span>;
  return <span className={muted ? "text-slate-400 line-through" : "text-slate-800"}>{fmtPrice(c.price)}</span>;
}

function AdminCell({ item, model, line, batchMode, onEdit }) {
  const cur = item.prices[model] || {};
  const pend = line && !line.remove ? line.prices?.[model] : null;
  const changed = pend && (pend.price !== cur.price || pend.na !== cur.na || pend.note !== cur.note);
  const locked = !batchMode && item.status === "live" && !isPh(cur);
  return [
    <td key="p" onClick={onEdit} data-testid={`price-cell-${item.item_id}-${model}`}
        className={`cursor-pointer px-2 py-1.5 text-right tabular-nums hover:bg-sky-50 ${changed ? "bg-amber-50" : ""}`}
        title={locked ? "Live price — click to edit the note; change the price through a change batch" : "Click to edit"}>
      <CellView c={cur} muted={changed} />
      {changed && <div className="text-amber-700"><CellView c={pend} /></div>}
      {locked && <Lock className="ml-1 inline h-2.5 w-2.5 text-slate-300" />}
    </td>,
    <td key="n" className="px-2 py-1.5 text-[11px] text-slate-400">{(changed ? pend : cur).note}</td>,
  ];
}

export function PriceListAdmin() {
  const [d, setD] = useState(null);
  const [mode, setMode] = useState("");
  const [q, setQ] = usePersistedState("price-list-admin.q", "");
  const [onlyFix, setOnlyFix] = usePersistedState("price-list-admin.only-fix", false);
  const [dlg, setDlg] = useState(null);
  const load = useCallback(() => api.get("/price-list/admin").then(({ data }) => setD(data))
    .catch((e) => toast.error(errMsg(e, "Could not load the price list"))), []);
  useEffect(() => { load(); }, [load]);

  const batch = d?.batches.find((b) => b.batch_id === mode) || null;
  const groups = useMemo(() => {
    const t = q.trim().toLowerCase();
    return groupByCategory((d?.items || []).filter((i) => i.status !== "removed"
      && (!t || `${i.name} ${i.unit}`.toLowerCase().includes(t))
      && (!onlyFix || MODELS.some((m) => isPh(i.prices[m])))));
  }, [d, q, onlyFix]);
  if (!d) return <Loader2 className="mx-auto mt-12 h-6 w-6 animate-spin text-slate-300" />;

  const drafts = d.items.filter((i) => i.status === "draft");
  const cats = [...new Set(d.items.map((i) => i.category))];
  const run = async (fn, ok) => { try { await fn(); if (ok) toast.success(ok); load(); } catch (e) { toast.error(errMsg(e, "Action failed")); } };

  const saveCell = (item, model, cell) => batch
    ? run(() => api.put(`/price-list/batches/${batch.batch_id}/lines/${item.item_id}`, { prices: { [model]: cell } }), "Added to the batch")
    : run(() => api.patch(`/price-list/items/${item.item_id}`, { prices: { [model]: cell } }), "Saved");
  const openCell = (item, model) => {
    if (batch && batch.status !== "draft") return toast.info("This batch is scheduled — cancel it to make changes");
    if (batch && item.status !== "live") return toast.info("Draft items are edited directly — switch to the current list");
    if (!batch && d.pending[item.item_id]) toast.info("This item has a pending change in a batch");
    const ln = batch?.lines?.[item.item_id];
    setDlg({ kind: "cell", item, model, cell: ln && !ln.remove ? ln.prices[model] : item.prices[model] });
  };
  const exportFile = (format) => run(async () => {
    const { data } = await api.get("/price-list/export", { params: { format }, responseType: "blob" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(data); a.download = `price-list.${format}`; a.click();
  });

  return (
    <div className="space-y-3" data-testid="price-list-admin">
      <Card className="border-slate-200 p-4 shadow-sm">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="mr-2 font-heading text-base font-semibold text-slate-800">Price List</h3>
          <select value={mode} onChange={(e) => setMode(e.target.value)} data-testid="price-mode"
                  className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm">
            <option value="">Editing: current list</option>
            {d.batches.map((b) => <option key={b.batch_id} value={b.batch_id}>{`Batch: ${b.name} · ${b.effective_date} · ${b.status}`}</option>)}
          </select>
          {d.can.edit && <Button size="sm" variant="outline" onClick={() => setDlg({ kind: "newBatch" })} data-testid="price-new-batch"><Plus className="h-3.5 w-3.5" /> New change batch</Button>}
          {batch && <>
            <Button size="sm" onClick={() => setDlg({ kind: "review" })} data-testid="price-review-open"><Send className="h-3.5 w-3.5" /> Review{batch.status === "draft" ? " & send" : ""}</Button>
            <Button size="sm" variant="outline" className="text-red-600" data-testid="price-batch-cancel"
                    onClick={() => window.confirm(batch.status === "scheduled" ? "Cancel this scheduled batch? Affected partners get a cancellation notice." : "Discard this draft batch?")
                      && run(() => api.post(`/price-list/batches/${batch.batch_id}/cancel`), "Batch cancelled").then(() => setMode(""))}>
              <XCircle className="h-3.5 w-3.5" /> Cancel batch</Button>
          </>}
          <div className="ml-auto flex flex-wrap gap-2">
            <PriceInsightsDialog d={d} />
            {d.can.create && <Button size="sm" variant="outline" onClick={() => setDlg({ kind: "item" })} data-testid="price-add-item"><Plus className="h-3.5 w-3.5" /> Add product</Button>}
            {d.can.create && drafts.length > 0 && <Button size="sm" variant="outline" data-testid="price-publish"
              onClick={() => window.confirm(`Publish ${drafts.length} draft item(s)? Partners see them immediately.`)
                && run(() => api.post("/price-list/items/publish", { item_ids: drafts.map((i) => i.item_id) }), "Published")}>
              Publish drafts ({drafts.length})</Button>}
            {d.can.approve && d.unannounced.length > 0 && <Button size="sm" variant="outline" data-testid="price-announce-new"
              onClick={() => window.confirm(`Email Partner Admins about ${d.unannounced.length} new item(s)?`)
                && run(() => api.post("/price-list/announce-new", { item_ids: d.unannounced }), "New-products email sent")}>
              <Mail className="h-3.5 w-3.5" /> New-products email ({d.unannounced.length})</Button>}
            {d.can.export && <>
              <Button size="sm" variant="outline" onClick={() => exportFile("csv")} data-testid="price-export-csv"
                      title="Item ID + the 7 columns + Category and Unit — fill in the blanks and import it back"><Download className="h-3.5 w-3.5" /> Export CSV</Button>
              <Button size="sm" variant="outline" onClick={() => exportFile("xlsx")} data-testid="price-export"><Download className="h-3.5 w-3.5" /> Excel</Button>
              <Button size="sm" variant="outline" onClick={() => setDlg({ kind: "import" })} data-testid="price-import"><Upload className="h-3.5 w-3.5" /> Import CSV / Excel</Button>
            </>}
          </div>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <div className="relative w-64"><Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="pl-8" data-testid="price-admin-search" /></div>
          <button type="button" onClick={() => setOnlyFix(!onlyFix)} data-testid="price-needs-correction"
                  className={`rounded-full border px-3 py-1 text-xs font-semibold ${onlyFix ? "border-red-300 bg-red-50 text-red-700" : "border-slate-200 text-red-600"}`}>
            Needs correction: {d.needs_correction}</button>
          {batch && <span className="text-xs text-amber-700" data-testid="price-batch-info">
            {Object.keys(batch.lines || {}).length} line(s) in this batch — click any live price to set its new value{batch.override ? " · urgent override" : ""}</span>}
        </div>
      </Card>

      <Card className="overflow-hidden border-slate-200 shadow-sm">
        <div className="max-h-[70vh] overflow-auto">
          <table className="w-full text-sm" data-testid="price-admin-table">
            <thead className="sticky top-0 z-10 bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
              <tr><th className="px-2 py-2 text-left">Item or Service</th>
                {MODELS.map((m) => [<th key={m} className="px-2 py-2 text-right">{MODEL_LABEL[m]} Price</th>, <th key={`${m}n`} className="px-2 py-2 text-left">{MODEL_LABEL[m]} Note</th>])}
                <th className="px-2 py-2" /></tr>
            </thead>
            <tbody>{groups.map((g) => [
              <tr key={g.category} className="bg-slate-100/70"><td colSpan={8} className="px-2 py-1.5 text-xs font-semibold uppercase tracking-wide text-slate-600">{g.category}</td></tr>,
              ...g.items.map((i) => {
                const ln = batch?.lines?.[i.item_id];
                return (
                  <tr key={i.item_id} className={`border-t border-slate-100 ${ln?.remove ? "bg-red-50/60 line-through" : ""}`} data-testid={`price-admin-row-${i.item_id}`}>
                    <td className={`px-2 py-1.5 ${i.parent_id ? "pl-6 text-slate-600" : "font-medium text-slate-800"}`}>
                      <button type="button" className="text-left hover:underline" onClick={() => d.can.edit && setDlg({ kind: "item", item: i })}>{i.name}</button>
                      {i.status === "draft" && <span className="ml-1.5 rounded bg-slate-200 px-1 text-[9px] font-semibold uppercase text-slate-600">draft</span>}
                      {d.pending[i.item_id] && !batch && <span className="ml-1.5 rounded bg-amber-100 px-1 text-[9px] font-semibold uppercase text-amber-700">pending change</span>}
                      {i.unit && <div className="text-[11px] font-normal text-slate-400">{i.unit}</div>}
                    </td>
                    {MODELS.map((m) => <AdminCell key={m} item={i} model={m} line={ln} batchMode={!!batch} onEdit={() => d.can.edit && openCell(i, m)} />)}
                    <td className="px-2 py-1.5 text-right">
                      {batch?.status === "draft" && i.status === "live" && (
                        <button type="button" className="text-[11px] text-red-600 hover:underline" data-testid={`price-remove-${i.item_id}`}
                                onClick={() => run(() => ln ? api.delete(`/price-list/batches/${batch.batch_id}/lines/${i.item_id}`)
                                  : api.put(`/price-list/batches/${batch.batch_id}/lines/${i.item_id}`, { remove: true }))}>
                          {ln ? "Undo" : "Remove"}</button>)}
                      {!batch && i.status === "draft" && d.can.delete && (
                        <button type="button" onClick={() => window.confirm(`Delete draft "${i.name}"?`) && run(() => api.delete(`/price-list/items/${i.item_id}`), "Deleted")}
                                data-testid={`price-delete-${i.item_id}`}><Trash2 className="h-3.5 w-3.5 text-slate-400 hover:text-red-600" /></button>)}
                    </td>
                  </tr>
                );
              })])}
            </tbody>
          </table>
        </div>
      </Card>

      {dlg?.kind === "cell" && <CellDialog target={{ ...dlg, batch }} onClose={() => setDlg(null)} onSave={(c) => saveCell(dlg.item, dlg.model, c)} />}
      {dlg?.kind === "item" && <ItemDialog item={dlg.item} categories={cats} parents={d.items.filter((x) => !x.parent_id && x.item_id !== dlg.item?.item_id)}
        onClose={() => setDlg(null)}
        onSave={(body) => dlg.item ? run(() => api.patch(`/price-list/items/${dlg.item.item_id}`, body), "Saved")
          : run(() => api.post("/price-list/items", body), "Added as draft")} />}
      {dlg?.kind === "newBatch" && <NewBatchDialog rules={d.rules} canOverride={d.can.override} onClose={() => setDlg(null)}
        onCreated={(b) => { setDlg(null); load().then(() => setMode(b.batch_id)); }} />}
      {dlg?.kind === "review" && batch && <ReviewDialog batch={batch} canApprove={d.can.approve} onClose={() => setDlg(null)} onDone={() => { setDlg(null); load(); }} />}
      {dlg?.kind === "import" && <ImportDialog batches={d.batches} isSuper={d.is_super} onClose={() => setDlg(null)} onDone={() => { setDlg(null); load(); }} />}
    </div>
  );
}
